import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The experience gallery, one image at a time.
 *
 * Built on a native scroll-snap track rather than a transform slider: swiping on
 * a phone and flicking a trackpad then come for free and keep the platform's own
 * inertia, and the arrows and dots only have to move the scroll position. The
 * consequence is that scroll — not React state — is the source of truth for which
 * image is showing, so `index` is read back off the track rather than set ahead
 * of it.
 */
export function GalleryCarousel({ images, name }: { images: string[]; name: string }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);

  // A single image has nothing to page through, so it renders as a plain image
  // without arrows or a lone dot.
  const isCarousel = images.length > 1;

  useEffect(() => {
    const track = trackRef.current;
    if (!track || !isCarousel) return;

    // Fires through the whole flick, but the work is a division and a state set
    // that mostly no-ops; rAF-throttling it would only lag the dots behind the
    // image.
    const onScroll = () => {
      const width = track.clientWidth;
      if (width === 0) return;
      const next = Math.round(track.scrollLeft / width);
      setIndex(Math.min(Math.max(next, 0), images.length - 1));
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    return () => track.removeEventListener('scroll', onScroll);
  }, [images.length, isCarousel]);

  /** Wraps, so the arrows never dead-end on the first or last image. */
  const goTo = useCallback(
    (next: number) => {
      const track = trackRef.current;
      if (!track) return;
      const count = images.length;
      const target = ((next % count) + count) % count;
      const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollTo({ left: target * track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
    },
    [images.length],
  );

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(index + 1);
    }
  };

  if (images.length === 0) return null;

  return (
    <div
      className='tw:group/gallery tw:mt-12'
      role='group'
      aria-roledescription='carousel'
      aria-label={`${name} gallery`}
      onKeyDown={isCarousel ? onKeyDown : undefined}
    >
      <div className='tw:relative'>
        <div
          ref={trackRef}
          // tabIndex so the track is reachable by keyboard: it is a scroll
          // container, and the arrow keys handled above are the only way to page
          // it without tabbing on to the buttons.
          tabIndex={isCarousel ? 0 : undefined}
          className={
            'ed-carousel-track tw:flex tw:w-full tw:overflow-x-auto tw:outline-none ' +
            // The radius clips the images because the track is its own scroll
            // container; the shadow sits on the same box so it traces the corners.
            'tw:rounded-xl tw:shadow-lg ' +
            'tw:focus-visible:ring-2 tw:focus-visible:ring-ed-gold/60 ' +
            (isCarousel ? 'tw:snap-x tw:snap-mandatory' : '')
          }
        >
          {images.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`${name} ${i + 1}`}
              // The first image is the one in view; the rest are off to the side
              // and can wait until they are paged to.
              loading={i === 0 ? 'eager' : 'lazy'}
              className='tw:aspect-[4/3] tw:w-full tw:shrink-0 tw:snap-center tw:object-cover'
            />
          ))}
        </div>

        {isCarousel && (
          <>
            <ArrowButton side='left' label='Previous image' onClick={() => goTo(index - 1)} />
            <ArrowButton side='right' label='Next image' onClick={() => goTo(index + 1)} />
          </>
        )}
      </div>

      {isCarousel && (
        <>
          <div className='tw:mt-4 tw:flex tw:items-center tw:justify-center tw:gap-2'>
            {images.map((_, i) => (
              <button
                key={i}
                type='button'
                onClick={() => goTo(i)}
                aria-label={`Go to image ${i + 1}`}
                aria-current={i === index}
                className={
                  'tw:h-1.5 tw:rounded-full tw:transition-all tw:duration-300 ' +
                  (i === index ? 'tw:w-6 tw:bg-ed-gold' : 'tw:w-1.5 tw:bg-ed-umber/25 tw:hover:bg-ed-umber/50')
                }
              />
            ))}
          </div>

          {/* Screen readers get the position as a message rather than as a row of
              dots they would have to count through. */}
          <p className='tw:sr-only' aria-live='polite'>
            Image {index + 1} of {images.length}
          </p>
        </>
      )}
    </div>
  );
}

/** One of the two paging arrows, overlaid on the image. */
function ArrowButton({ side, label, onClick }: { side: 'left' | 'right'; label: string; onClick: () => void }) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type='button'
      onClick={onClick}
      aria-label={label}
      className={
        'tw:absolute tw:top-1/2 tw:flex tw:size-10 tw:-translate-y-1/2 tw:items-center tw:justify-center ' +
        'tw:bg-ed-cream/90 tw:text-ed-black tw:opacity-0 tw:transition tw:duration-300 tw:hover:bg-ed-cream ' +
        'tw:focus-visible:opacity-100 tw:group-hover/gallery:opacity-100 tw:group-focus-within/gallery:opacity-100 ' +
        // Touch has no hover to reveal the arrows with, so below the hover
        // breakpoint they stay put.
        'tw:max-md:opacity-100 ' +
        (side === 'left' ? 'tw:left-3' : 'tw:right-3')
      }
    >
      <Icon className='tw:size-5' />
    </button>
  );
}
